// Phase 2 PR1 of the adaptive-lookup-field refactor. See
// `docs/audits/adaptive-field-refactor-proposal.md` §3.5.
//
// Small memoized helper that answers "is the lookup backing this AI-suggestion
// field currently multi-select?" without every caller re-fetching the
// collection doc. The AI-enrichment write path in ReferenceEditor's
// handleApplyAISuggestions consults it before dispatching a suggested value
// into local state, so a field flipped single→multi in Phase 3 (Q2 six-field
// promotion) doesn't need editor code changes to route the AI array through
// the right handler — only its useState type + its LookupFieldSelect →
// LookupMultiSelect swap.
//
// Memoization is `Promise`-shaped so concurrent callers share the same
// in-flight Firestore read. The cache is coarse-grained per-collection —
// small (~one entry per lookup) and only ever grows with the collection set;
// invalidated by `invalidateLookupMultiSelectCache()` when the admin flips a
// collection's `isMultiSelect` flag in the Lookup Manager, matching the
// admin-write-triggered invalidation pattern the AI enrichment side already
// uses (`bumpLookupsVersion` in firebase.ts).

import { getLookupCollectionFull } from './firebase';
import { ENRICH_FIELD_LOOKUPS } from './enrichEnumFields';

const isMultiSelectByCollection = new Map<string, Promise<boolean>>();

/**
 * Returns whether the target lookup collection is multi-select, memoized by
 * collection name. Unknown fields (not in ENRICH_FIELD_LOOKUPS) and missing
 * collection docs both resolve to `false` — the safer default, since routing
 * an array through a scalar setter is a louder failure than the reverse.
 */
export async function getLookupMultiSelectFor(
  fieldKey: string,
): Promise<boolean> {
  const collectionName = ENRICH_FIELD_LOOKUPS[fieldKey];
  if (!collectionName) return false;

  const cached = isMultiSelectByCollection.get(collectionName);
  if (cached) return cached;

  const pending = (async () => {
    try {
      const full = await getLookupCollectionFull(collectionName);
      return !!full?.isMultiSelect;
    } catch (err) {
      console.error(
        'getLookupMultiSelectFor: load failed for',
        collectionName,
        err,
      );
      return false;
    }
  })();

  isMultiSelectByCollection.set(collectionName, pending);
  // If the fetch rejects (thrown before the try), drop the entry so callers
  // retry rather than pinning a rejected Promise. The inner catch above
  // already resolves to false for the normal-failure case, so this is belt
  // and braces for surprising async paths.
  pending.catch(() => isMultiSelectByCollection.delete(collectionName));
  return pending;
}

/** Drop the cache; call when a collection's isMultiSelect flag flips. */
export function invalidateLookupMultiSelectCache(collectionName?: string) {
  if (collectionName) {
    isMultiSelectByCollection.delete(collectionName);
  } else {
    isMultiSelectByCollection.clear();
  }
}
